import React, { useCallback, useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Hand,
  SquareCheck,
  Users,
} from "lucide-react";
import type { RaisedHand } from "../../hooks/useRaisedHands";
import { useAuth } from "../../context/AuthProvider";
import toast from "react-hot-toast";
import { useWebexMeetingStore } from "../../store/webex.store";
import { useMeetingStore } from "../../store/meeting.store";
import { socket } from "../mediasoup/socket";
import logger from "../../utils/logger";

interface ParticipantParty {
  name: string;
  type: "petitioner" | "respondent";
}
interface Participant {
  userId: string;
  caseNumber: string;
  cnr: string;
  parties?: ParticipantParty[];
  caseTitle: string;
  name: string;
  status: string;
}

const getCaseTitle = (participant: Participant): string => {
  // NEW DATA
  if (participant.parties?.length) {
    const petitioner = participant.parties.find(
      (party) => party.type === "petitioner",
    )?.name;

    const respondent = participant.parties.find(
      (party) => party.type === "respondent",
    )?.name;

    if (petitioner && respondent) {
      return `${petitioner} vs ${respondent}`;
    }

    if (petitioner) {
      return petitioner;
    }

    if (respondent) {
      return respondent;
    }
  }

  // OLD DATA
  if (participant.caseTitle) {
    return participant.caseTitle;
  }

  return "N/A";
};

const getParticipantKey = (participant: Participant) =>
  `${participant.status}:${participant.userId}:${participant.cnr}`;

const uniqueParticipants = (items: Participant[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = getParticipantKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getVisibleCount = () => {
  if (globalThis.window !== undefined) {
    if (globalThis.window.innerWidth < 640) return 1; // Mobile: 1 participant
    if (globalThis.window.innerWidth < 768) return 2; // Mobile: 2 participant
    if (globalThis.window.innerWidth < 1024) return 3; // Tablet: 3 participants
    if (globalThis.window.innerWidth < 1280) return 4; // Desktop: 4 participants
    return 5;
  }
  return 5;
};

interface ParticipantCardProps {
  handRaised: boolean;
  participant: Participant;
  selectedCase: any;
  onAdmit: (p: Participant) => void;
}

const ParticipantCard: React.FC<ParticipantCardProps> = ({
  participant,
  selectedCase,
  onAdmit,
  handRaised,
}) => {
  const showAdmit =
    selectedCase?.cnr === participant.cnr || participant.status === "lobby";

  return (
    <div className="bg-white bg-opacity-10 text-black rounded-lg hover:bg-opacity-20 transition-all duration-200 min-w-0">
      <div className="text-xs bg-blue-900 text-white p-1 font-semibold flex justify-between items-center">
        Case No. {participant.caseNumber}
        {showAdmit && (
          <SquareCheck
            onClick={() => onAdmit(participant)}
            className="w-4 h-4 text-white cursor-pointer"
          />
        )}
      </div>
      <div className="text-center">
        <b className="text-sm font-semibold">CNR: {participant.cnr}</b>
        <div className="text-sm truncate">
          {participant.status === "lobby"
            ? participant.caseTitle
            : getCaseTitle(participant)}
        </div>
      </div>
      <div className="text-xs bg-blue-900 text-white p-1 font-medium flex items-center gap-2">
        <span className="truncate">{participant.name}</span>
        {handRaised && (
          <span
            title="Hand raised"
            role="img"
            aria-label="Hand raised"
            className="ml-auto shrink-0 rounded-full bg-amber-400 p-1 text-amber-950"
          >
            <Hand className="h-2 w-2" aria-hidden="true" />
          </span>
        )}
      </div>
    </div>
  );
};

const HeaderParticipants = ({
  raisedHands = [],
}: {
  raisedHands?: RaisedHand[];
}) => {
  const selectedCase = useMeetingStore((s) => s.selectedCase);
  const userData = useMeetingStore((s) => s.userData);
  const { user } = useAuth();
  const [startIndex, setStartIndex] = useState(0);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [visibleCount, setVisibleCount] = useState(getVisibleCount());
  const lobbyMembers = useWebexMeetingStore((s) => s.lobbyMembers);
  const meeting = useWebexMeetingStore((s) => s.meeting);

  const handleAdvocateLeft = useCallback((data: any) => {
    setParticipants((prev: any) =>
      prev.filter((a: any) => a._id !== data.advocates._id),
    );
  }, []);

  useEffect(() => {
    if (!socket || !userData?.roomNo) return;
    socket.emit("join_room", {
      meetingId: `${userData.roomNo}_${userData.roomId}`,
      userId: userData._id,
      role: userData.role,
    });
    const handleAdvocateRequests = (data: any) => {
      logger.log("advocate_requests:", data);
      setParticipants(uniqueParticipants(data || []));
    };

    socket.on("advocate_requests", handleAdvocateRequests);
    return () => {
      socket.off("advocate_requests", handleAdvocateRequests);
    };
  }, [
    socket,
    userData?._id,
    userData?.role,
    userData?.roomId,
    userData?.roomNo,
  ]);

  useEffect(() => {
    if (!socket) return;
    socket.on("advocate-left", handleAdvocateLeft);
    return () => {
      socket.off("advocate-left", handleAdvocateLeft);
    };
  }, [socket]);

  const handleResize = useCallback(() => {
    const count = getVisibleCount();
    setVisibleCount(count);
    const maxStart = Math.max(0, participants?.length - count);
    setStartIndex((prev) => Math.min(prev, maxStart));
  }, [participants?.length]);

  React.useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const nextSlide = () => {
    const maxStart = Math.max(0, participants?.length - visibleCount);
    setStartIndex((prev) => Math.min(prev + 1, maxStart));
  };

  const prevSlide = () => {
    setStartIndex((prev) => Math.max(prev - 1, 0));
  };

  const canGoNext = startIndex + visibleCount < participants?.length;
  const canGoPrev = startIndex > 0;

  const mappedLobbyMembers: Participant[] = (lobbyMembers || []).map(
    (m: any) => ({
      userId: m.id,
      caseNumber: selectedCase?.caseNumber,
      cnr: selectedCase?.cnr,
      caseTitle: "Guest User Waiting",
      name: m?.name || "Guest",
      status: "lobby",
    }),
  );

  const handleAdmitToAdvocate = useCallback(
    (participant: Participant) => {
      const message =
        participant.status === "lobby" &&
        participant.caseTitle === "Guest User Waiting"
          ? `${participant.name} admitted`
          : `Advocate for CNR ${participant.cnr} admitted`;

      if (
        participant.status === "lobby" &&
        participant.caseTitle === "Guest User Waiting"
      ) {
        meeting?.members?.admitMembers([participant.userId]);
      }

      socket?.emit("judge_accept_request", {
        meetingId: `${userData?.roomNo}_${userData?.roomId}`,
        actionBy: user?.id,
        userId: participant.userId,
        roomNo: userData?.roomNo,
        cnr: participant.cnr,
      });

      toast.success(message, { id: "advocate-admitted" });
    },
    [meeting, user, userData],
  );

  if (participants.length === 0 && mappedLobbyMembers.length === 0) {
    return (
      <header className="bg-[#0d0840] text-white p-4 shadow-lg w-full h-[100px]">
        <div className="mx-auto flex items-center justify-center h-full">
          <Users className="w-6 h-6 mr-2" />
          <span className="text-lg font-medium">No participants waiting</span>
        </div>
      </header>
    );
  }

  const allParticipants = uniqueParticipants([
    ...participants,
    ...mappedLobbyMembers,
  ]);
  const visibleParticipants = allParticipants.slice(
    startIndex,
    startIndex + visibleCount,
  );

  return (
    <header className="w-full shadow-lg h-[100px]">
      <div className="mx-auto p-2">
        {/* Navigation Container */}
        <div className="flex  items-center">
          {/* Left Navigation */}
          <button
            onClick={prevSlide}
            disabled={!canGoPrev}
            className="p-2 bg-blue-900 rounded-full text-white bg-opacity-20 hover:bg-opacity-30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 mr-4"
            aria-label="Previous participants"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Participants Container */}
          <div className="flex-1 overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-2">
              {visibleParticipants.map((participant) => (
                <ParticipantCard
                  key={getParticipantKey(participant)}
                  participant={participant}
                  handRaised={raisedHands.some(
                    (hand) =>
                      hand.userId === participant.userId &&
                      hand.cnr === participant.cnr,
                  )}
                  selectedCase={selectedCase}
                  onAdmit={handleAdmitToAdvocate}
                />
              ))}
            </div>
          </div>

          {/* Right Navigation */}
          <button
            onClick={nextSlide}
            disabled={!canGoNext}
            className="p-2 bg-blue-900 rounded-full text-white  bg-opacity-20 hover:bg-opacity-30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 ml-4"
            aria-label="Next participants"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </header>
  );
};

export default HeaderParticipants;
